<template>
  <router-view />
  <GlobalPreloader :active="loaderActive" :message="loaderMessage" />
  <ToastContainer :toasts="toasts" @remove="removeToast" />
</template>

<script>
import GlobalPreloader from "@/components/ui/GlobalPreloader.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import { loaderState } from "@/utils/globalLoader";
import {
  createToastEntry,
  getToastIcon,
  normalizeToastArgs,
  scheduleToastDismiss,
} from "@/utils/toast";

/**
 * App.vue — Root application component
 *
 * Renders the Vue Router outlet and a global toast notification container.
 * Listens for custom `show-toast` window events so any child can trigger toasts
 * without prop drilling or a store.
 *
 * @module App
 * @component
 */
export default {
  name: 'App',
  components: {
    GlobalPreloader,
    ToastContainer,
  },

  computed: {
    loaderActive() {
      return loaderState.active;
    },
    loaderMessage() {
      return loaderState.message;
    },
  },

  /**
   * Reactive data for the root app.
   * @returns {Object}
   * @property {Array<{id: number, title: string, message: string, type: string, icon: string}>} toasts - Active toast notifications to show
   * @property {number} toastIdCounter - Incrementing id for each new toast (used for keys and removal)
   */
  data() {
    return {
      toasts: [],
      toastIdCounter: 0
    };
  },

  mounted() {
    // Listen for toast events from anywhere in the app
    window.addEventListener('show-toast', this.handleToastEvent);
  },

  beforeUnmount() {
    window.removeEventListener('show-toast', this.handleToastEvent);
  },

  methods: {
    /**
     * Handles the global `show-toast` custom event and forwards to showToast.
     * Expects event.detail: { title, message?, type? }.
     * @param {CustomEvent} event - Must have event.detail with at least title
     */
    handleToastEvent(event) {
      const { title, message, type } = event.detail || {};
      this.showToast(title, message, type);
    },

    /**
     * @param {string} a - title, or type when using (type, title, message)
     * @param {string} [b]
     * @param {string} [c]
     */
    showToast(a, b, c) {
      const { type, title, message } = normalizeToastArgs(a, b, c);
      const toast = createToastEntry({
        id: ++this.toastIdCounter,
        type,
        title,
        message,
      });

      this.toasts.push(toast);
      scheduleToastDismiss(toast, (id) => this.removeToast(id));
    },

    /**
     * Removes a toast by id (used by ToastContainer and auto-dismiss).
     * @param {number} id - Toast id from toastIdCounter
     */
    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index > -1) {
        this.toasts.splice(index, 1);
      }
    },

    /**
     * Returns the Font Awesome icon class for a toast type.
     * @param {string} type - One of 'success' | 'error' | 'warning' | 'info'
     * @returns {string} Font Awesome class (e.g. 'fa-solid fa-check-circle')
     */
    getToastIcon,
  }
};
</script>

<style>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

:root {
  --background-color1: rgb(9, 9, 9);
  --background-color2: black;
  --background-color3: rgba(26, 26, 26, 0.338);
  --text-color1: white;
  --text-color2: rgba(255, 255, 255, 0.76);
  --border-color-hover: aqua;
  --border-color-hover1: rgba(0, 255, 255, 0.26);
  --box-shadow: rgba(218, 218, 218, 0.377);
  --button-color: black;
  --button-border-color: white;
  --role-item: rgba(0, 255, 255, 0.42);
  --border-color-hover3: rgb(0, 151, 0);
  --text-color3: black;
  --gradient: linear-gradient(135deg, #0097A7 0%, #00BCD4 25%, #4DD0E1 50%, #B2EBF2 75%, #e0f7fac9 100%);
  --primary-color: gold;
  
  /* New CSS Variables - Light Mode Colors (Initial) */
  --dashboard-bg: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  --card-bg: linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%);
  --light-overlay: rgba(15, 23, 42, 0.95);
  --light-card: rgba(30, 41, 59, 0.8);
  --loader-border: rgba(59, 130, 246, 0.1);
  --loader-color: #3b82f6;
  --heading-color: #f1f5f9;
  --subtext-color: #94a3b8;
  --gradient-border: linear-gradient(90deg, #3b82f6, #8b5cf6, #10b981);
  --profile-border: #3b82f6;
  --profile-shadow: rgba(59, 130, 246, 0.3);
  --online-indicator: #10b981;
  --name-gradient: linear-gradient(90deg, #f8fafc, #94a3b8);
  --meta-bg: rgba(255, 255, 255, 0.05);
  --role-icon: #3b82f6;
  --number-icon: #8b5cf6;
  --approved-bg: rgba(16, 185, 129, 0.2);
  --approved-color: #10b981;
  --approved-border: rgba(16, 185, 129, 0.3);
  --pending-bg: rgba(245, 158, 11, 0.2);
  --pending-color: #f59e0b;
  --pending-border: rgba(245, 158, 11, 0.3);
  --rejected-bg: rgba(239, 68, 68, 0.2);
  --rejected-color: #ef4444;
  --rejected-border: rgba(239, 68, 68, 0.3);
  --unknown-bg: rgba(148, 163, 184, 0.2);
  --unknown-color: #94a3b8;
  --unknown-border: rgba(148, 163, 184, 0.3);
  --status-pulse: rgba(245, 158, 11, 0.4);
  --edit-gradient: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  --edit-shadow: rgba(59, 130, 246, 0.3);
  --action-primary-bg: rgba(59, 130, 246, 0.2);
  --action-primary-color: #3b82f6;
  --action-secondary-bg: rgba(139, 92, 246, 0.2);
  --action-secondary-color: #8b5cf6;
  --action-success-bg: rgba(16, 185, 129, 0.2);
  --action-success-color: #10b981;
  --action-warning-bg: rgba(245, 158, 11, 0.2);
  --action-warning-color: #f59e0b;
  --tab-active-bg: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(30, 41, 59, 0.7) 100%);
  --tab-active-border: rgba(59, 130, 246, 0.5);
  --tab-icon-bg: rgba(59, 130, 246, 0.2);
  --tab-icon-color: #3b82f6;
  --notification-success-border: #10b981;
  --notification-error-border: #ef4444;
  --notification-info-border: #3b82f6;
  --notification-warning-border: #f59e0b;
  --notification-bg: linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%);
  --card-hover-border: rgba(59, 130, 246, 0.3);
  --placeholder-icon: rgba(59, 130, 246, 0.5);
  --section-icon: #3b82f6;
}

[data-theme="dark"] {
  --background-color1: rgb(241, 241, 241);
  --background-color2: white;
  --background-color3: rgb(218, 218, 218);
  --text-color1: black;
  --text-color2: rgba(48, 48, 48, 0.76);
  --box-shadow: rgba(0, 0, 0, 0.1);
  --border-color-hover: rgb(0, 151, 0);
  --border-color-hover1: rgba(1, 145, 1, 0.356);
  --button-color: white;
  --button-border-color: black;
  --role-item: rgba(0, 128, 0, 0.461);
  --border-color-hover3: aqua;
  --text-color3: white;
  --primary-color: maroon;
  --gradient: linear-gradient(135deg, #4CAF50 0%, #2E7D32 25%, #81C784 50%, #E8F5E8 75%, #ffffffc4 100%);
  
  /* New CSS Variables - Dark Mode Colors (Reversed) */
  --dashboard-bg: linear-gradient(135deg, #f0f4f8 0%, #e1e8f0 100%);
  --card-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.9) 100%);
  --light-overlay: rgba(255, 255, 255, 0.95);
  --light-card: rgba(255, 255, 255, 0.9);
  --loader-border: rgba(0, 151, 0, 0.1);
  --loader-color: rgb(0, 151, 0);
  --heading-color: #1a202c;
  --subtext-color: #4a5568;
  --gradient-border: linear-gradient(90deg, rgb(0, 151, 0), aqua, #2E7D32);
  --profile-border: rgb(0, 151, 0);
  --profile-shadow: rgba(0, 151, 0, 0.3);
  --online-indicator: #2E7D32;
  --name-gradient: linear-gradient(90deg, #1a202c, #4a5568);
  --meta-bg: rgba(0, 0, 0, 0.05);
  --role-icon: rgb(0, 151, 0);
  --number-icon: aqua;
  --approved-bg: rgba(46, 125, 50, 0.2);
  --approved-color: #2E7D32;
  --approved-border: rgba(46, 125, 50, 0.3);
  --pending-bg: rgba(245, 158, 11, 0.2);
  --pending-color: #f59e0b;
  --pending-border: rgba(245, 158, 11, 0.3);
  --rejected-bg: rgba(220, 38, 38, 0.2);
  --rejected-color: #dc2626;
  --rejected-border: rgba(220, 38, 38, 0.3);
  --unknown-bg: rgba(113, 128, 150, 0.2);
  --unknown-color: #718096;
  --unknown-border: rgba(113, 128, 150, 0.3);
  --status-pulse: rgba(245, 158, 11, 0.4);
  --edit-gradient: linear-gradient(135deg, rgb(0, 151, 0) 0%, #2E7D32 100%);
  --edit-shadow: rgba(0, 151, 0, 0.3);
  --action-primary-bg: rgba(0, 151, 0, 0.2);
  --action-primary-color: rgb(0, 151, 0);
  --action-secondary-bg: rgba(0, 255, 255, 0.2);
  --action-secondary-color: aqua;
  --action-success-bg: rgba(46, 125, 50, 0.2);
  --action-success-color: #2E7D32;
  --action-warning-bg: rgba(245, 158, 11, 0.2);
  --action-warning-color: #f59e0b;
  --tab-active-bg: linear-gradient(135deg, rgba(0, 151, 0, 0.2) 0%, rgba(255, 255, 255, 0.9) 100%);
  --tab-active-border: rgba(0, 151, 0, 0.5);
  --tab-icon-bg: rgba(0, 151, 0, 0.2);
  --tab-icon-color: rgb(0, 151, 0);
  --notification-success-border: #2E7D32;
  --notification-error-border: #dc2626;
  --notification-info-border: rgb(0, 151, 0);
  --notification-warning-border: #f59e0b;
  --notification-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 240, 240, 0.95) 100%);
  --card-hover-border: rgba(0, 151, 0, 0.3);
  --placeholder-icon: rgba(0, 151, 0, 0.5);
  --section-icon: rgb(0, 151, 0);
}

.pre-footer-banner {
  width: 100%;
  height: auto;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 1;
}

.pre-footer-banner img {
  width: 100%;
  height: 800px;
  display: block;
  object-fit: cover;
}

/* =====================================
   🦶 FOOTER LAYOUT FIX
===================================== */
.main-footer {
  position: relative;
  margin-top: -100px;
  z-index: 10;
}
</style>
